/**
 * 🔗 spaceIdFromInput — turn ANYTHING that identifies a space into its id.
 *
 * Nina 08-15: the /admin "set a space's current song" field took a bare space
 * id, so using it meant opening the Spaces table and copying a uuid out by
 * hand, while the thing already on the clipboard — the share URL — was
 * rejected. Every share surface hands out `/game-step?gameId=<uuid>`
 * (ShareButton.js:32), which is exactly the string an admin has to hand.
 *
 * Accepts, in order of precision:
 *   · a bare uuid                     `5a10f0fa-b82c-4069-baf3-e0776b7bccf8`
 *   · any URL with a gameId param     `https://dev.myspaice.ai/game-step?gameId=…&loading=true`
 *   · the share-wrapper paths         `/space/<id>` · `/s/<id>` · `/embed/<id>`
 *   · a uuid anywhere in the string   (the catch-all — covers formats not yet invented)
 *
 * Tolerant of the ways a pasted link actually arrives: no scheme
 * (`dev.myspaice.ai/game-step?…`), a path with no host (`/game-step?…`), a
 * trailing slash or `#fragment`, surrounding whitespace, and the quotes or
 * angle brackets that chat clients wrap links in.
 *
 * ⭐ NEVER throws and never returns empty for a non-empty input: anything it
 * cannot parse comes back TRIMMED but otherwise untouched. That is what makes
 * this safe to drop in front of an existing field — the old "type a bare id"
 * behaviour is the fallback, so no input that worked before can start failing.
 * (Every live space id is uuid-shaped — measured 50/50 on 08-15 — but the
 * pass-through means a non-uuid id would still reach the server unharmed.)
 */

// Canonical uuid, anywhere in a string. Not anchored on purpose — the
// any-uuid-anywhere pass is the last-resort catch-all.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const UUID_EXACT_RE = new RegExp(`^${UUID_RE.source}$`, 'i');

// The share-wrapper + embed routes (App.js): /space/:gameId, /s/:gameId,
// /embed/:gameId. `/game-step` is NOT here — it carries its id as a query
// param, which the gameId= pass above handles.
const PATH_RE = /\/(?:space|s|embed)\/([^/?#\s]+)/i;

/** `gameId` / `game_id` / `id` as a query param, case-insensitively. */
const PARAM_RE = /[?&](?:gameid|game_id)=([^&#\s]+)/i;

export const isSpaceId = (v) => UUID_EXACT_RE.test(String(v || '').trim());

export function spaceIdFromInput(input) {
  const raw = String(input == null ? '' : input).trim();
  if (!raw) return '';

  // Strip the wrappers a pasted link picks up in chat clients / markdown.
  const cleaned = raw
    .replace(/^[<("'`]+/, '')
    .replace(/[>)"'`]+$/, '')
    .trim();
  if (!cleaned) return raw;

  // 1. Already an id — the original behaviour, and the cheapest check.
  if (UUID_EXACT_RE.test(cleaned)) return cleaned;

  // 2. An explicit gameId param wins over anything else in the string. Decoded
  //    because a link that has been through a redirector arrives percent-encoded.
  const param = cleaned.match(PARAM_RE);
  if (param) {
    let v = param[1];
    try { v = decodeURIComponent(v); } catch (e) { /* malformed escape — use it raw */ }
    v = v.trim();
    if (v) return v;
  }

  // 3. A share-wrapper / embed path segment.
  const path = cleaned.match(PATH_RE);
  if (path && path[1]) {
    let v = path[1];
    try { v = decodeURIComponent(v); } catch (e) { /* as above */ }
    v = v.trim();
    if (v) return v;
  }

  // 4. Catch-all: a uuid anywhere in the string. Deliberately last, so a URL
  //    that carries several never beats an explicit gameId=.
  const loose = cleaned.match(UUID_RE);
  if (loose) return loose[0];

  // 5. Unparseable → hand back what was typed. See the ⭐ note above.
  return cleaned;
}

export default spaceIdFromInput;
